import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Calendar, AlertCircle, CheckCircle2, Search, 
  ClipboardList, Wrench, Rocket, TrendingUp, DollarSign, 
  Users, Target, BarChart3, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const problems = [
  { text: 'Revenue is flat or declining', icon: AlertCircle },
  { text: 'Marketing efforts aren\'t generating leads', icon: AlertCircle },
  { text: 'Operations feel chaotic or inefficient', icon: AlertCircle },
  { text: 'You\'re working harder but not seeing results', icon: AlertCircle },
  { text: 'Customers aren\'t coming back', icon: AlertCircle },
];

const solutions = [
  'Audit your entire business operation',
  'Identify revenue leaks and missed opportunities',
  'Rebuild your sales and marketing systems',
  'Streamline operations for efficiency',
  'Create measurable growth strategies',
];

const processSteps = [
  { 
    step: 1, 
    title: 'Discovery', 
    description: 'We dig into your business to understand where you are and where you want to be.',
    icon: Search
  },
  { 
    step: 2, 
    title: 'Diagnosis', 
    description: 'We identify the exact bottlenecks, inefficiencies, and gaps holding you back.',
    icon: ClipboardList
  },
  { 
    step: 3, 
    title: 'Strategy', 
    description: 'We build a clear action plan with priorities, timelines, and measurable goals.',
    icon: Target
  },
  { 
    step: 4, 
    title: 'Execution', 
    description: 'We help you implement changes that drive real, lasting results.',
    icon: Rocket
  },
];

const outcomes = [
  { text: 'Clear visibility into what\'s working and what\'s not', icon: BarChart3 },
  { text: 'Systems that generate leads consistently', icon: TrendingUp },
  { text: 'Streamlined operations that save time and money', icon: Zap },
  { text: 'A roadmap for sustainable growth', icon: Target },
  { text: 'Increased revenue and profitability', icon: DollarSign },
  { text: 'Confidence in your business decisions', icon: Users },
];

const businessTurnaroundSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Business Turnaround",
  "description": "Business turnaround consulting services. We identify what's broken and rebuild the systems that drive visibility, conversion, and revenue.",
  "provider": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  },
  "areaServed": "US",
  "serviceType": "Business Consulting and Turnaround"
};

const BusinessTurnaroundPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="business-turnaround-page">
      <SEO 
        title="Business Turnaround | Fearless IT"
        description="Fix what's costing you money. We step into your business, identify what's broken, and rebuild the systems that drive visibility, conversion, and revenue."
        keywords="business turnaround, business consulting, fix business, increase revenue, business audit, business strategy, business systems"
        url="/solutions/business-turnaround"
        structuredData={businessTurnaroundSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 sm:pt-28 sm:pb-24 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
            Fix What's Costing You Money
          </h1>
          <p className="text-xl sm:text-2xl text-indigo-200 mb-10 max-w-3xl mx-auto leading-relaxed">
            We step into your business, identify what's broken, and rebuild the systems that drive visibility, conversion, and revenue.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-audit">
              <Button className="bg-indigo-500 hover:bg-indigo-600 text-white px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                Start My Business Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/consultation">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                <Calendar className="mr-2 h-5 w-5" />
                Book a Strategy Call
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
                Something Isn't Working. You Can Feel It.
              </h2>
              <p className="text-lg text-slate-gray mb-8 leading-relaxed">
                You've put in the work. You've tried different strategies. But the results aren't there.
              </p>
              <p className="text-lg text-slate-gray mb-6">
                If any of this sounds familiar:
              </p>
              <div className="space-y-4">
                {problems.map((problem, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    </div>
                    <span className="text-carbon-black font-medium">{problem.text}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-slate-gray mt-8 leading-relaxed">
                <strong className="text-carbon-black">It's not about working harder.</strong> It's about fixing the systems that are supposed to be working for you.
              </p>
            </div>
            
            {/* Visual Accent */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                <div className="w-64 h-64 rounded-full bg-red-100 flex items-center justify-center">
                  <div className="w-48 h-48 rounded-full bg-red-200 flex items-center justify-center">
                    <div className="w-32 h-32 rounded-full bg-red-300 flex items-center justify-center">
                      <TrendingUp className="h-16 w-16 text-red-600 rotate-180" />
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-red-400 animate-pulse"></div>
                <div className="absolute -bottom-2 -left-6 w-8 h-8 rounded-full bg-red-500 animate-pulse delay-300"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl p-8 sm:p-12 border-l-4 border-indigo-500">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
              We Find the Problem. Then We Fix It.
            </h2>
            <p className="text-lg text-slate-gray mb-8 leading-relaxed">
              At Fearless IT, we don't just consult. We roll up our sleeves, get into the details, and help you rebuild what's broken.
            </p>
            <p className="text-lg text-slate-gray mb-6">
              Here's what we do:
            </p>
            <div className="space-y-4 mb-8">
              {solutions.map((solution, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-carbon-black font-medium">{solution}</span>
                </div>
              ))}
            </div>
            <p className="text-lg text-slate-gray leading-relaxed">
              <strong className="text-indigo-600">No fluff. No generic advice.</strong> Just a clear path from where you are to where you need to be.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              How We Turn Things Around
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              A proven process that gets to the root of the problem and builds real solutions.
            </p>
          </div>
          
          {/* Desktop: Horizontal Stepper */}
          <div className="hidden md:block">
            <div className="relative">
              {/* Connecting Line */}
              <div className="absolute top-12 left-0 right-0 h-1 bg-indigo-200 mx-24"></div>
              
              <div className="grid grid-cols-4 gap-6">
                {processSteps.map((step, idx) => {
                  const IconComponent = step.icon;
                  return (
                    <div key={idx} className="relative flex flex-col items-center text-center">
                      <div className="w-24 h-24 rounded-full bg-indigo-500 flex items-center justify-center mb-6 relative z-10 shadow-lg">
                        <div className="text-center">
                          <span className="text-2xl font-bold text-white">{step.step}</span>
                        </div>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                        <IconComponent className="h-6 w-6 text-indigo-600" />
                      </div>
                      <h3 className="text-xl font-bold text-carbon-black mb-2">{step.title}</h3>
                      <p className="text-sm text-slate-gray leading-relaxed">{step.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Mobile: Vertical Stepper */}
          <div className="md:hidden space-y-6">
            {processSteps.map((step, idx) => {
              const IconComponent = step.icon;
              return (
                <div key={idx} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-bold text-white">{step.step}</span>
                    </div>
                    {idx < processSteps.length - 1 && (
                      <div className="w-0.5 h-full bg-indigo-200 my-2"></div>
                    )}
                  </div>
                  <div className="pb-8">
                    <div className="flex items-center gap-3 mb-2">
                      <IconComponent className="h-5 w-5 text-indigo-600" />
                      <h3 className="text-lg font-bold text-carbon-black">{step.title}</h3>
                    </div>
                    <p className="text-slate-gray">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              What You Walk Away With
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Real, tangible improvements that impact your bottom line.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {outcomes.map((outcome, idx) => {
              const IconComponent = outcome.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center justify-center mb-4">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-carbon-black">{outcome.text}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-indigo-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Turn Things Around?
          </h2>
          <p className="text-xl text-indigo-200 mb-10 max-w-2xl mx-auto leading-relaxed">
            Stop guessing. Stop spinning your wheels. Let's figure out exactly what's holding your business back and fix it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-audit">
              <Button className="bg-white text-indigo-600 hover:bg-gray-100 px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                Get Your Free Business Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/consultation">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-5 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto">
                <Calendar className="mr-2 h-5 w-5" />
                Book a Strategy Call
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default BusinessTurnaroundPage;
